import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  GhlConnection,
  GhlPipeline,
  LocationOption,
} from "@/components/admin/ghl/ghlSectionShared";

export async function fetchGhlSectionData(sb: SupabaseClient, organizationId: string): Promise<{
  connections: GhlConnection[];
  locations: LocationOption[];
  knownProductTypes: string[];
  pipelinesByConn: Record<string, GhlPipeline[]>;
}> {
  const [connRes, locRes] = await Promise.all([
    sb
      .from("ghl_connections")
      .select("id, ghl_location_id, company_name, webhook_url, is_active, last_synced_at")
      .eq("organization_id", organizationId)
      .order("company_name"),
    sb.from("locations").select("id, name").order("name"),
  ]);

  if (connRes.error) throw connRes.error;
  if (locRes.error) throw locRes.error;

  const connections = (connRes.data ?? []) as GhlConnection[];
  const locations = (locRes.data ?? []) as LocationOption[];

  let knownProductTypes: string[] = [];
  const ptRes = await sb.from("ghl_pipelines").select("product_type").not("product_type", "is", null);
  if (!ptRes.error) {
    knownProductTypes = [
      ...new Set(
        (ptRes.data ?? []).map((r) => (r as { product_type: string }).product_type).filter(Boolean),
      ),
    ].sort();
  }

  const pipelinesByConn: Record<string, GhlPipeline[]> = {};
  if (connections.length) {
    const connIds = connections.map((c) => c.id);
    const pipeRes = await sb
      .from("ghl_pipelines")
      .select("id, ghl_connection_id, ghl_pipeline_id, pipeline_name, location_id, product_type, is_active")
      .in("ghl_connection_id", connIds)
      .order("pipeline_name");

    if (pipeRes.error) throw pipeRes.error;

    for (const conn of connections) pipelinesByConn[conn.id] = [];
    for (const p of (pipeRes.data ?? []) as GhlPipeline[]) {
      pipelinesByConn[p.ghl_connection_id] ??= [];
      pipelinesByConn[p.ghl_connection_id].push(p);
    }
  }

  return { connections, locations, knownProductTypes, pipelinesByConn };
}
